import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Building2, Search, Download, TrendingUp, TrendingDown } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { VociPicker, SummaryTable, DetailTable, eur } from "@/components/produzionecommittente/PcTables";

const monthStart = () => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1).toLocaleDateString("sv-SE"); };
const monthEnd = () => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth() + 1, 0).toLocaleDateString("sv-SE"); };

export default function ProduzioneCommittentePage() {
  const [committenti, setCommittenti] = useState([]);
  const [cid, setCid] = useState("");
  const [from, setFrom] = useState(monthStart());
  const [to, setTo] = useState(monthEnd());
  const [voci, setVoci] = useState([]);
  const [selected, setSelected] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const params = { committente_id: cid, date_from: from, date_to: to, keys: selected.join("||") };

  const exportXlsx = async () => {
    setExporting(true);
    try {
      const r = await api.get("/production-voci/export", { params, responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(r.data);
      a.download = `produzione_${from}_${to}.xlsx`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch { toast.error("Esportazione non riuscita"); }
    finally { setExporting(false); }
  };

  useEffect(() => { api.get("/committenti").then((r) => setCommittenti(r.data || [])).catch(() => {}); }, []);
  useEffect(() => {
    setData(null);
    if (!cid || !from || !to) { setVoci([]); return; }
    api.get("/production-voci/options", { params: { committente_id: cid, date_from: from, date_to: to } })
      .then((r) => { setVoci(r.data.voci || []); setSelected((s) => s.filter((k) => (r.data.voci || []).some((v) => v.key === k))); })
      .catch(() => setVoci([]));
  }, [cid, from, to]);

  const run = async () => {
    setLoading(true);
    try {
      const r = await api.get("/production-voci", { params });
      setData(r.data);
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Calcolo non riuscito"); }
    finally { setLoading(false); }
  };

  const cname = committenti.find((c) => c.id === cid)?.name;
  return (
    <div className="space-y-6" data-testid="produzione-committente-page">
      <div>
        <h1 className="flex items-center gap-3 text-3xl font-black tracking-tight text-slate-900"><Building2 className="h-7 w-7 text-[#0052FF]" /> Produzione per Committente</h1>
        <p className="mt-2 text-sm text-slate-600">Scegli committente, periodo e una o più voci (es. installazione modem) per vedere quanto è stato fatturato con quelle attività.</p>
      </div>
      <div className="space-y-4 rounded-sm border border-slate-200 bg-white p-6">
        <div className="grid gap-4 md:grid-cols-4">
          <div className="md:col-span-2">
            <Label>Committente</Label>
            <Select value={cid} onValueChange={setCid}>
              <SelectTrigger className="rounded-sm" data-testid="pc-committente"><SelectValue placeholder="Scegli committente" /></SelectTrigger>
              <SelectContent>{committenti.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Dal</Label><Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="rounded-sm" data-testid="pc-from" /></div>
          <div><Label>Al</Label><Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="rounded-sm" data-testid="pc-to" /></div>
        </div>
        {cid && <VociPicker voci={voci} selected={selected} onChange={setSelected} />}
        <div className="flex flex-wrap gap-2">
          <Button onClick={run} disabled={!cid || loading} className="rounded-sm bg-[#0052FF] hover:bg-[#0043d1]" data-testid="pc-run-btn">
            <Search className="mr-2 h-4 w-4" /> {loading ? "Calcolo..." : "Calcola"}
          </Button>
          <Button variant="outline" onClick={exportXlsx} disabled={!cid || exporting} className="rounded-sm" data-testid="pc-export-btn">
            <Download className="mr-2 h-4 w-4" /> {exporting ? "Esportazione..." : "Scarica Excel"}
          </Button>
        </div>
      </div>
      {data && (
        <>
          <div className="grid gap-4 sm:grid-cols-3">
            <Kpi label={`Fatturato ${cname || ""}`} value={eur(data.total_amount)} testid="pc-kpi-amount" strong>
              {data.previous && <Compare cur={data.total_amount} prev={data.previous} />}
            </Kpi>
            <Kpi label="Lavori" value={data.total_jobs} testid="pc-kpi-jobs" />
            <Kpi label="Quantità totale" value={data.total_quantity} testid="pc-kpi-qty" />
          </div>
          <SummaryTable data={data} />
          <div>
            <div className="mb-3 text-xs font-bold uppercase tracking-[0.15em] text-slate-500">Dettaglio lavori ({data.rows.length})</div>
            <DetailTable rows={data.rows} />
          </div>
        </>
      )}
    </div>
  );
}

function Kpi({ label, value, testid, strong, children }) {
  return (
    <div className="rounded-sm border border-slate-200 bg-white p-5">
      <div className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">{label}</div>
      <div className={`mt-2 font-mono text-2xl font-black ${strong ? "text-[#0052FF]" : "text-slate-900"}`} data-testid={testid}>{value}</div>
      {children}
    </div>
  );
}

const fmtD = (s) => s.split("-").reverse().join("/");

function Compare({ cur, prev }) {
  const diff = cur - prev.total_amount;
  const pct = prev.total_amount ? (diff / prev.total_amount) * 100 : null;
  const up = diff >= 0;
  const Icon = up ? TrendingUp : TrendingDown;
  return (
    <div className="mt-3 border-t border-slate-100 pt-3 text-xs" data-testid="pc-compare">
      <div className="text-slate-500">Mese prec. ({fmtD(prev.date_from)} – {fmtD(prev.date_to)}): <span className="font-mono font-semibold text-slate-700" data-testid="pc-compare-prev">{eur(prev.total_amount)}</span></div>
      <div className={`mt-1 flex items-center gap-1 font-mono font-bold ${up ? "text-emerald-600" : "text-red-600"}`} data-testid="pc-compare-diff">
        <Icon className="h-3.5 w-3.5" /> {up ? "+" : "−"}{eur(Math.abs(diff))}{pct !== null && ` (${up ? "+" : "−"}${Math.abs(pct).toLocaleString("it-IT", { maximumFractionDigits: 1 })}%)`}
      </div>
    </div>
  );
}
